import type { LanguageType } from '@/pages/opinion/types/common';
import { useMemo } from 'react';
import { useIntl } from 'umi';
import type { SortBy } from 'tea-component/lib/table/addons';
import getBaseColumns from '../utils';
import { newsMediaColumnsFunc } from '../columns/newsMedia';
import { useMetricsChannel } from '@op/hooks/useMetricsChannel';
import useLanguage from '@op/hooks/useLanguage';
import { useGetTopicFilter } from '@op/hooks/useTranslateTopic';

export function useNewsMediaColumns({
  language,
  renderKey = '',
  sort,
  setSort,
  showSentiment = false,
  total,
  logCommentMap,
  logLoading,
  hasSearch,
  showIEGGPr,
  gameInfo,
  setLanguage,
}: {
  language: LanguageType;
  renderKey?: string | { origin: string[]; zh: string[]; en: string[] };
  sort: SortBy<any>;
  setSort: (sort: SortBy<any>) => void;
  showSentiment?: boolean;
  total?: number;
  logCommentMap?: { [key: string]: string };
  logLoading?: boolean;
  hasSearch?: boolean;
  showIEGGPr?: boolean;
  gameInfo: any;
  setLanguage?: (language: LanguageType) => void;
}) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const MergeChannelIconElement = useMetricsChannel({ gameInfo });
  const { topicList } = useGetTopicFilter({ gameInfo });

  const columns = useMemo(() => {
    let keys = [
      'article',
      'uvpm',
      'engagement',
      'release_time',
      'data_source',
      'type_of_coverage',
      'beat',
      'topic',
    ];

    const IEGGPRKeys = ['uvpm', 'data_source', 'type_of_coverage', 'beat'];

    if (!showIEGGPr) {
      keys = keys.filter((val) => !IEGGPRKeys.includes(val));
    }

    const columnsMap = newsMediaColumnsFunc({
      formatMessage,
      language,
      renderKey,
      topicList,
      sort,
      setSort,
      showSentiment,
      total,
      logCommentMap,
      logLoading,
      MergeChannelIconElement,
      setLanguage,
      formatOptionsMessage,
    });

    return getBaseColumns(keys, columnsMap);
  }, [
    formatMessage,
    language,
    renderKey,
    topicList,
    sort,
    showSentiment,
    total,
    logCommentMap,
    logLoading,
  ]);

  return {
    columns,
    sortColumns: hasSearch
      ? ['release_time']
      : ['engagement', 'release_time', 'uvpm'],
  };
}
